import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { updateMetaTags, getCanonicalUrl, SEO_CONFIG } from '@/utils/seo';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  noindex?: boolean;
  ogImage?: string;
  ogType?: 'website' | 'product';
}

/**
 * SEOHead component for managing page-level SEO meta tags
 * Updates document head dynamically when props change
 */
export const SEOHead = ({
  title,
  description,
  canonicalPath,
  noindex = false,
  ogImage,
  ogType = 'website',
}: SEOHeadProps) => {
  const location = useLocation();
  
  useEffect(() => {
    const fullTitle = title 
      ? `${title} | ${SEO_CONFIG.BRAND_NAME}`
      : SEO_CONFIG.DEFAULT_TITLE;
    
    const fullDescription = description || SEO_CONFIG.DEFAULT_DESCRIPTION;
    
    // Use provided canonical path or current path
    const canonical = canonicalPath 
      ? getCanonicalUrl(canonicalPath)
      : getCanonicalUrl(location.pathname + location.search);
    
    updateMetaTags({
      title: fullTitle,
      description: fullDescription,
      canonicalUrl: canonical,
      noindex,
      ogImage: ogImage || SEO_CONFIG.DEFAULT_OG_IMAGE,
      ogType,
    });
    
    // Cleanup: reset to defaults when component unmounts
    return () => {
      // Don't reset - let the next page set its own meta
    };
  }, [title, description, canonicalPath, noindex, ogImage, ogType, location.pathname, location.search]);
  
  return null;
};

export default SEOHead;
